import { useState } from 'react';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ChevronDown, ChevronRight, Lock, Info, Check } from 'lucide-react';

const CATEGORY_ORDER = [
  'Property Use & Occupancy',
  'Rent Payments',
  'Deposit',
  'Property Care',
  'Safety & Prevention',
  'Restrictions',
  'Access & Inspections',
  'End of Tenancy',
  'Landlord Obligations',
  'General',
  'Student Let',
  'Guarantor Terms',
];

export const ClauseLibrary = ({
  clauses,
  selectedClauses,
  autoIncluded,
  clauseOverrides,
  onToggle,
  onOverrideChange,
}) => {
  const [openCategories, setOpenCategories] = useState(() => {
    const cats = [...new Set(clauses.map((c) => c.category_label))];
    return Object.fromEntries(cats.map((c) => [c, true]));
  });
  const [expandedBodies, setExpandedBodies] = useState({});

  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    const items = clauses.filter((c) => c.category_label === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});

  const toggleCategory = (cat) =>
    setOpenCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));

  const toggleBody = (id) =>
    setExpandedBodies((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="space-y-3">
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} className="border rounded-lg overflow-hidden">
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            onClick={() => toggleCategory(cat)}
          >
            <span className="font-medium text-sm text-gray-700">{cat}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">
                {items.filter((c) => selectedClauses.includes(c.id) || autoIncluded.includes(c.id)).length}/{items.length}
              </span>
              {openCategories[cat] ? (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-400" />
              )}
            </div>
          </button>

          {openCategories[cat] && (
            <div className="divide-y">
              {items.map((clause) => {
                const isAutoIncluded = autoIncluded.includes(clause.id);
                const isSelected = selectedClauses.includes(clause.id) || isAutoIncluded;
                const isExpanded = expandedBodies[clause.id];
                const overrideVals = clauseOverrides[clause.id] || {};

                return (
                  <div key={clause.id} className="px-4 py-3">
                    {isAutoIncluded ? (
                      <div className="flex items-start gap-3">
                        <div className="pt-0.5">
                          <div className="w-9 flex items-center justify-center">
                            <Lock className="h-4 w-4 text-[#1e3a5f]" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center flex-wrap gap-2">
                            <span className="text-sm font-medium text-gray-800 leading-tight">
                              {clause.title}
                            </span>
                            <Badge variant="outline" className="text-xs text-blue-700 border-blue-300 bg-blue-50 py-0">
                              Auto-included
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Desktop View */}
                        <div className="hidden md:flex items-start gap-3">
                          <div className="pt-0.5">
                            <Switch
                              checked={isSelected}
                              onCheckedChange={() => onToggle(clause.id)}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center flex-wrap gap-2">
                              <span className="text-sm font-medium text-gray-800 leading-tight">
                                {clause.title}
                              </span>
                              {clause.default_on && (
                                <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-300 bg-emerald-50 py-0">
                                  Recommended
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Mobile View */}
                        <div className="md:hidden">
                          <button
                            type="button"
                            onClick={() => onToggle(clause.id)}
                            className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center flex-wrap gap-2 text-left">
                              <span className="text-sm font-medium text-gray-800 leading-tight">
                                {clause.title}
                              </span>
                              {clause.default_on && (
                                <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-300 bg-emerald-50 py-0">
                                  Recommended
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {isSelected && <Check className="h-4 w-4 text-[#1e3a5f]" />}
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSelected ? 'bg-[#1e3a5f] border-[#1e3a5f]' : 'border-gray-300'}`}>
                                {isSelected && <Check className="h-4 w-4 text-white" />}
                              </div>
                            </div>
                          </button>
                        </div>
                      </>
                    )}

                    {/* Clause content for both desktop and mobile */}
                    {!isAutoIncluded && (
                      <div className="mt-2">
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-600 flex items-center gap-1 text-xs"
                          onClick={() => toggleBody(clause.id)}
                          title="Preview clause text"
                        >
                          <Info className="h-3 w-3" />
                          Preview clause
                          {isExpanded ? (
                            <ChevronDown className="h-3 w-3" />
                          ) : (
                            <ChevronRight className="h-3 w-3" />
                          )}
                        </button>

                        {isExpanded && (
                          <div className="mt-2 p-3 bg-gray-50 rounded-md">
                            <p className="text-xs text-gray-600 leading-relaxed">
                              {clause.body}
                            </p>
                          </div>
                        )}

                        {isSelected && clause.has_variables && clause.variable_fields?.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {clause.variable_fields.map((field) => (
                              <div key={field.key} className="flex items-center gap-2">
                                <Label className="text-xs text-gray-600 min-w-[140px]">
                                  {field.label}
                                </Label>
                                <Input
                                  type={field.type === 'number' ? 'number' : 'text'}
                                  value={overrideVals[field.key] ?? ''}
                                  onChange={(e) =>
                                    onOverrideChange(clause.id, field.key, e.target.value)
                                  }
                                  className="h-7 text-sm w-24"
                                  placeholder={field.type === 'number' ? '0' : '...'}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {isAutoIncluded && (
                      <p className="text-xs text-blue-600 mt-0.5">
                        Automatically included based on your tenancy details
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
